import {
  Checkbox,
  ChoiceGroup,
  DatePicker,
  DayOfWeek,
  DefaultButton,
  defaultDatePickerStrings,
  Dropdown,
  IChoiceGroupOption,
  IDropdownOption,
  IPanelStyles,
  MessageBar,
  MessageBarType,
  Panel,
  PanelType,
  Pivot,
  PivotItem,
  PrimaryButton,
  SearchBox,
  Spinner,
  SpinnerSize,
  Text,
  TextField,
} from '@fluentui/react';
import { Context } from '@micro-frontend-react/employee-experience/lib/Context';
import { IEmployeeExperienceContext } from '@micro-frontend-react/employee-experience/lib/IEmployeeExperienceContext';
import { useUser } from '@micro-frontend-react/employee-experience/lib/useUser';
import * as React from 'react';
import { Context as ReactContext, FormEvent, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import {
  AttachmentItem,
  DeleteAttachmentRequest,
  PrivacyApprovalArtifact,
  StoreAttachmentRequest,
  StoreAttachmentsResponse,
} from '../../../../Common/CommonModels/Attachments';
import * as Constants from '../../../../Common/CommonModels/Constants';
import { EgressOptionEnum } from '../../../../Common/CommonModels/EgressOptionEnum';
import { EgressOptionType } from '../../../../Common/CommonModels/EgressOptionTypeEnum';
import { AttachmentsForm } from '../../../../Common/Components/AttachmentsForm';
import {
  onRenderLabelEffectiveDatedCdm,
  onRenderLabelEgressOptions,
  onRenderLabelGraphEgressOptions,
  onRenderLabelPrivacyApprovalArtifacts,
  onRenderLabelPrivacyApprovalDate,
  onRenderLabelServiceBusNamespace,
  onRenderLabelServiceBusTopic,
  onRenderLabelPrivacyTeam,
  onRenderLabelOtherPrivacyTeam,
} from '../../../../Common/Components/Labels';
import { SubscriberRecord } from '../../../../Common/ConsumerModels/SubscriberRecord';
import { SubscriberRequestRecord } from '../../../../Common/ConsumerModels/SubscriberRequestRecord';
import { addRequiredAttributes, removeSubscriptionNodeFields, addKeyAttributes, markApprovedAttributes, removeApprovedAttributes } from '../../../../Common/Helper/subscriptionNodeHelper';
import { useGetAvailableSubscriptionAttributes } from '../../../../Hooks/Consumer/useGetAvailableSubscriptionAttributes';
import { useGetApprovedSubscriptionAttributes } from '../../../../Hooks/Consumer/useGetApprovedSubscriptionAttributes';
import { useGetAllPrivacyTeams } from '../../../../Hooks/Consumer/useGetAllPrivacyTeams';
import { TenantSubscriptions } from './TenantSubscriptions';

type AddSubscriberRequestPanelProps = {
  isOpen: boolean;
  dismissPanel(): void;
  setShowConfirmationPopup(val: boolean): void;
  setShowSuccessMessage(val: boolean): void;
  setShowErrorMessage(val: boolean): void;
  subscriberId: string;
  subscriberRequestDefault?: SubscriberRequestRecord;
  subscriberRecord: SubscriberRecord;
};

const stickyFooterPanelStyles: Partial<IPanelStyles> = {
  footer: {
    borderTop: '1px solid #e5e5e5',
  },
  content: {
    overflowX: 'hidden',
  },
};

export function AddSubscriberRequestPanel(props: AddSubscriberRequestPanelProps): React.ReactElement {
  const {
    isOpen,
    dismissPanel,
    setShowConfirmationPopup,
    setShowSuccessMessage,
    setShowErrorMessage,
    subscriberId,
    subscriberRequestDefault = {
      /*
       * Create default values for a new Add Subscriber Request,
       * otherwise use the selected subscriber request for previous values in a Draft request
       */
      id: uuidv4(),
      name: '',
      description: '',
      subscriberId: '',
      requestorUpn: '',
      environment: 0,
      status: 'Draft',
      egressOptions: [] as number[],
      graphEgressOptions: 0,
      serviceBusNamespace: '',
      serviceBusTopic: '',
      effectiveDatedCDM: '',
      privacyApprovalArtifacts: [] as PrivacyApprovalArtifact[],
      privacyApprovalDate: new Date(),
      additionalNotes: '',
      subscribedAttributes: [] as undefined,
      privacyTeamName: '',
      customPrivacyTeam: '',
      graphqlAccessType: '',
    },
  } = props;

  const user = useUser();
  const { httpClient } = React.useContext(Context as ReactContext<IEmployeeExperienceContext>);
  const [selectedPivotKey, setSelectedPivotKey] = React.useState('BasicDetails');

  // Subscriber Request State
  const [id] = useState<string>(subscriberRequestDefault.id);
  const [name, setName] = useState<string>(subscriberRequestDefault.name);
  const [description, setDescription] = useState<string>(subscriberRequestDefault.description);
  const [environment, setEnvironment] = useState<number>(subscriberRequestDefault.environment);
  const [status] = useState<string>(subscriberRequestDefault.status);
  const [egressOptions, setEgressOptions] = useState<number[]>(subscriberRequestDefault.egressOptions);
  const [egressType, setEgressType] = useState<EgressOptionType>(EgressOptionType.Others);
  const [serviceBusNamespace, setServiceBusNamespace] = useState<string>(subscriberRequestDefault.serviceBusNamespace);
  const [serviceBusTopic, setServiceBusTopic] = useState<string>(subscriberRequestDefault.serviceBusTopic);
  const [effectiveDatedCDM, setEffectiveDatedCDM] = useState<string>(subscriberRequestDefault.effectiveDatedCDM);
  const [attachmentList, setAttachmentList] = React.useState<AttachmentItem[]>(
    subscriberRequestDefault.privacyApprovalArtifacts as AttachmentItem[]
  );
  const [graphEgressOptions, setGraphEgressOptions] = useState<number>(subscriberRequestDefault.graphEgressOptions);
  const [deleteAttachmentRequestList, setDeleteAttachmentRequestList] = React.useState<DeleteAttachmentRequest[]>([]);
  const [privacyApprovalDate, setPrivacyApprovalDate] = React.useState<Date | undefined>(
    new Date(subscriberRequestDefault.privacyApprovalDate)
  );
  const [additionalNotes, setAdditionalNotes] = useState<string>(subscriberRequestDefault.additionalNotes);

  const [isServiceBusInfoLoading, setIsServiceBusInfoLoading] = useState(false);

  const [confidentialityAgreementAccepted, setConfidentialityAgreementAccepted] = useState(false);
  const [hasClickedAgreementLink, setHasClickedAgreementLink] = useState(false);
  const [showAgreementWarning, setShowAgreementWarning] = useState(false);
  const [privacyTeam, setPrivacyTeam] = useState<string | undefined>(undefined);
  const [customPrivacyTeam, setCustomPrivacyTeam] = useState<string>(" ");
  const [graphqlAccessType, setGraphqlAccessType] = useState<string>("");

  // Attribute Subscription State
  const [searchQuery, setSearchQuery] = React.useState<string>('');
  const [
    formattedSubscriptionNodes,
    setFormattedSubscriptionNodes,
    filteredSubscriptionNodes,
    setFilteredSubscriptionNodes,
    checkedAttributeCount,
    setCheckedAttributeCount,
    isAttributeSubscriptionLoading,
  ] = useGetAvailableSubscriptionAttributes(
    subscriberRequestDefault.subscribedAttributes,
    environment,
    egressOptions,
    searchQuery
  );

  const [
    approvedSubscriptionNodes,
    ,
    ,
    ,
    isApprovedAttributeSubscriptionLoading,
  ] = useGetApprovedSubscriptionAttributes(environment, subscriberId, '');

  const isRenewalRequest = subscriberRequestDefault?.requestType?.toLowerCase() === 'renewal';

  const displayFormattedNodes = useMemo(
    () => isRenewalRequest
      ? formattedSubscriptionNodes
      : markApprovedAttributes(formattedSubscriptionNodes, approvedSubscriptionNodes, egressOptions),
    [formattedSubscriptionNodes, approvedSubscriptionNodes, egressOptions, isRenewalRequest]
  );

  const displayFilteredNodes = useMemo(
    () => isRenewalRequest
      ? filteredSubscriptionNodes
      : markApprovedAttributes(filteredSubscriptionNodes, approvedSubscriptionNodes, egressOptions),
    [filteredSubscriptionNodes, approvedSubscriptionNodes, egressOptions, isRenewalRequest]
  );

  const displayCheckedCount = useMemo(() => {
    let approvedCount = 0;
    displayFormattedNodes.forEach((t) =>
      t.children.forEach((c) =>
        c.children.forEach((a) => {
          if (a.isApprovedAttribute) approvedCount++;
        })
      )
    );
    return checkedAttributeCount + approvedCount;
  }, [displayFormattedNodes, checkedAttributeCount]);

  const [rawTeams] = useGetAllPrivacyTeams(false);

  // Subscriber Request Errors
  const [nameError, setNameError] = useState<boolean>(false);
  const [descriptionError, setDescriptionError] = useState<boolean>(false);
  const [egressOptionsError, setEgressOptionsError] = useState<boolean>(false);
  const [serviceBusNamespaceError, setServiceBusNamespaceError] = useState<boolean>(false);
  const [serviceBusTopicError, setServiceBusTopicError] = useState<boolean>(false);
  const [effectiveDatedCDMError, setEffectiveDatedCDMError] = useState<boolean>(false);
  const [privacyApprovalDateError, setPrivacyApprovalDateError] = useState<boolean>(false);
  const [additionalNotesError, setAdditionalNotesError] = useState<boolean>(false);
  const [isPrivacyTeamError, setIsPrivacyTeamError] = useState<boolean>(false);
  const [isCustomPrivacyTeamError, setIsCustomPrivacyTeamError] = useState<boolean>(false);
  const [graphEgressOptionsError, setGraphEgressOptionsError] = useState<boolean>(false);

  // Pivot Errors
  const [pivotChangeError, setPivotChangeError] = useState<boolean>(false);
  const [envClientError, setEnvClientError] = useState<boolean>(false);
  const [envRenewalError, setEnvRenewalError] = useState<boolean>(false);

  // Button State
  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState<boolean>(true);
  const [isSaveAsDraftButtonDisabled, setIsSaveAsDraftButtonDisabled] = useState<boolean>(true);
  const [isServiceBusEgressOptionSelected, setIsServiceBusEgressOptionSelected] = useState<boolean>(false);
  const [isGraphQLEgressOptionSelected, setIsGraphQLEgressOptionSelected] = useState<boolean>(false);
  const [graphQLInfoMessage, setGraphQLInfoMessage] = React.useState<string | undefined>(undefined);

  // Check form validity after each render

  useEffect(() => {
    const envError = getEnvironmentErrorMessage(environment);
    const hasNonApprovedSelectedAttributes = checkedAttributeCount > 0;

    const skipPrivacyRequirements =
      environment === 1 &&
      isOnlyEO14117Selected(displayFormattedNodes);

    // Save as draft logic (unchanged)
    if (
      name.length > 0 &&
      !nameError &&
      !descriptionError &&
      !egressOptionsError &&
      !isServiceBusInfoLoading &&
      !serviceBusNamespaceError &&
      !effectiveDatedCDMError &&
      !serviceBusTopicError &&
      !privacyApprovalDateError &&
      !additionalNotesError &&
      !envError &&
      !isPrivacyTeamError
    ) {
      setIsSaveAsDraftButtonDisabled(false);
    } else {
      setIsSaveAsDraftButtonDisabled(true);
    }

    // Add privacy skip option for EO14117 only
    if (
      name.length > 0 &&
      egressOptions.length > 0 &&
      effectiveDatedCDM.length > 0 &&
      (environment !== 1 || skipPrivacyRequirements || privacyApprovalDate !== undefined) &&
      (environment !== 1 || skipPrivacyRequirements || confidentialityAgreementAccepted) &&
      hasNonApprovedSelectedAttributes &&
      !nameError &&
      !descriptionError &&
      !egressOptionsError &&
      !serviceBusNamespaceError &&
      !effectiveDatedCDMError &&
      !serviceBusTopicError &&
      !additionalNotesError &&
      !envError &&
      !isPrivacyTeamError
    ) {
      if (
        environment == Constants.Request.ProductionEnvironment &&
        !skipPrivacyRequirements
      ) {
        if (attachmentList.length > 0 && !privacyApprovalDateError)
          setIsSubmitButtonDisabled(false);
        else
          setIsSubmitButtonDisabled(true);
      } else {
        setIsSubmitButtonDisabled(false);
      }
    } else {
      setIsSubmitButtonDisabled(true);
    }
  });

  useEffect(() => {
    if (subscriberRequestDefault && rawTeams?.length > 0) {
      const matchedTeam = rawTeams.find(
        (t) => t.teamName === subscriberRequestDefault.privacyTeamName
      );

      if (matchedTeam) {
        // Draft team exists in list
        setPrivacyTeam(matchedTeam.teamName);
      } else if (subscriberRequestDefault.privacyTeamName) {
        // Draft team was custom
        setPrivacyTeam("Other");
        setCustomPrivacyTeam(subscriberRequestDefault.privacyTeamName);
      }
    }
  }, [subscriberRequestDefault, rawTeams]);
  // const hasMSI = (env) => {
  //   var hasMSIs = false;
  //   userAccessTypes.map((accessType) => {if (accessType?.servicePrincipals?.length > 0) hasMSIs = true; });
  //   return hasMSIs;
  // };
  useEffect(() => {
  if (
    subscriberRequestDefault &&
    Array.isArray(subscriberRequestDefault.egressOptions) &&
    (subscriberRequestDefault.egressOptions.includes(5) || subscriberRequestDefault.egressOptions.includes(6))
  ) {
    setIsGraphQLEgressOptionSelected(true);
  } else {
    setIsGraphQLEgressOptionSelected(false);
  }
}, []);

  useEffect(() => {
    if (
      isRenewalRequest &&
      subscriberRequestDefault &&
      Array.isArray(subscriberRequestDefault.egressOptions) &&
      subscriberRequestDefault.egressOptions.includes(2)
    ) {
      setIsServiceBusEgressOptionSelected(true);
      if (!subscriberRequestDefault.serviceBusNamespace) {
        setServiceBusNamespaceError(true);
      }
      if (!subscriberRequestDefault.serviceBusTopic) {
        setServiceBusTopicError(true);
      }
    }
  }, []);

  useEffect(() => {
    if (
      isRenewalRequest &&
      subscriberRequestDefault &&
      Array.isArray(subscriberRequestDefault.egressOptions) &&
      subscriberRequestDefault.egressOptions.includes(4)
    ) {
      setEgressType(EgressOptionType.Kusto);
    }
  }, []);


  const environmentOptions: IChoiceGroupOption[] = [
    {
      key: 'NonProd',
      text: 'Non-Production',
      styles: {
        root: {
          margin: '0px 32px 0px 0px',
        },
      },
    },
    {
      key: 'Prod',
      text: 'Production',
      styles: {
        root: {
          margin: '0px 0px 0px 0px',
        },
      },
    },
  ];

  const egressTypeOptions: IChoiceGroupOption[] = [
    {
      key: EgressOptionType.Kusto,
      text: 'Kusto',
      styles: {
        root: {
          margin: '0px 32px 0px 0px',
        },
      },
    },
    {
      key: EgressOptionType.Others,
      text: 'Others',
      styles: {
        root: {
          margin: '0px 0px 0px 0px',
        },
      },
    },
  ];

  const egressionOptionsDropdown: IDropdownOption[] = [
    { key: 1, text: 'API' },
    { key: 2, text: 'Service Bus' },
    { key: 3, text: 'DataLake' },
    { key: 5, text: 'GraphQL API' },
    { key: 7, text: 'Snapshot' },
  ];

  const graphEgressionOptionsDropdown: IDropdownOption[] = [
    { key: 5, text: 'GraphQL Application' },
    { key: 6, text: 'GraphQL Delegated' },
  ];

  const effectiveDatedCDMOptions: IDropdownOption[] = [
    { key: 'Evaluated', text: 'Evaluated' },
    { key: 'Fullset', text: 'Fullset' },
  ];

  const privacyTeamOptions: IDropdownOption[] = [
  ...(rawTeams?.map(team => ({
     key: team.id,
     text: team.teamName
    })) ?? []),
    {
     key: 'Other',
     text: 'Other'
    }
  ];

  const handlePivotChange = (item: PivotItem) => {
    if (egressOptions.length > 0) {
      setSelectedPivotKey(item.props.itemKey);
    } else {
      setPivotChangeError(true);
    }
  };

  const onChangeName = (event: FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    setName(newValue);
  };

  const onChangeDescription = (event: FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    setDescription(newValue);
  };

  const onChangeEnvironment = React.useCallback(
    (event: React.SyntheticEvent<HTMLElement>, option: IChoiceGroupOption) => {
      var env = -1;
      if (option.key === 'Prod') {
        var env = 1;
      } else if (option.key === 'NonProd') {
        env = 0;
      }
      setEnvironment(env);
      getEnvironmentErrorMessage(env);
    },
    []
  );

  const onChangeEgressOptions = (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
    if (item && item.selected) {
      const cleaned = egressOptions.filter((key) => !(item.key === 5 && key === 6));
      setEgressOptions([...cleaned, item.key as number]);
      setEgressOptionsError(false);
      setPivotChangeError(false);
    }
    if (item && !item.selected) {
      const keysToRemove = item.key === 5 ? [5, 6] : [item.key];
      const newEgressOptions = egressOptions.filter((key) => !keysToRemove.includes(key));
      if (newEgressOptions.length === 0) {
        setEgressOptionsError(true);
      }
      setEgressOptions(newEgressOptions);
    }
    if (item && item.key === EgressOptionEnum.ServiceBus && item.selected) {
      setIsServiceBusEgressOptionSelected(true);
      setServiceBusNamespaceError(true);
      setServiceBusTopicError(true);
    }
    if (item && item.key === EgressOptionEnum.ServiceBus && !item.selected) {
      setIsServiceBusEgressOptionSelected(false);
      setServiceBusNamespace('');
      setServiceBusNamespaceError(false);
      setServiceBusTopic('');
      setServiceBusTopicError(false);
      }
    if (item && item.key === 5 && item.selected) {
      setIsGraphQLEgressOptionSelected(true);
    }
    if (item && item.key === 5 && !item.selected) {
      setIsGraphQLEgressOptionSelected(false);
    }
  };

  const onChangeEgressType = (event: React.SyntheticEvent<HTMLElement>, option: IChoiceGroupOption) => {
      const selectedEgressType = option.key as EgressOptionType;
      setEgressType(selectedEgressType);
      if (selectedEgressType === EgressOptionType.Kusto) {
        setEgressOptions([EgressOptionEnum.Kusto]);
        setEgressOptionsError(false);
        setPivotChangeError(false);
      } else if (selectedEgressType === EgressOptionType.Others) {
        setEgressOptions([]);
      }
  };

  const onChangeGraphQLEgressOptions = (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
    if (item) {
      setGraphEgressOptions(item.key as number);
      setGraphEgressOptionsError(false);
      setGraphQLInfoMessage(buildGraphEgressInfo([item.key as number]));
      if(item && item.key === 5){
        setGraphqlAccessType('Application');
      }
      if(item && item.key === 6){
        setGraphqlAccessType('Delegated');
      }
    }
    if (!item) {
      setGraphEgressOptionsError(true);
    }

  }

  const buildGraphEgressInfo = (selected: number[]): string | undefined => {
    const hasAllData = selected.includes(5);
    const hasUserOnly = selected.includes(6);

    if (hasAllData) {
      return "GraphQL Application permissions provides full access to all available data.";
    }
    if (hasUserOnly) {
      return "GraphQL Delegated permissions provides a user access token and allows access only to the signed‑in user’s data.";
    }
    return undefined;
  };

  const onChangeEffectiveDatedCDM = (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
    if (item) {
      setEffectiveDatedCDM(String(item.key));
    }
  };

  const onChangeServiceBusNamespace = (event: FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    setServiceBusNamespace(newValue);
  };

  const onChangeServiceBusTopic = (event: FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    setServiceBusTopic(newValue);
  };

  const onChangeAdditionalNotes = (event: FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    setAdditionalNotes(newValue);
  };

  const onChangePrivacyApprovalDate = (date: Date | null | undefined) => {
    setPrivacyApprovalDate(date);
  };

  const handleConfidentialityCheckbox = (ev: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
    if (!hasClickedAgreementLink) {
      setShowAgreementWarning(true);
      // Explicitly uncheck if user tried to check
      if (checked) {
        setConfidentialityAgreementAccepted(false);
      }
      return;
    }
    setShowAgreementWarning(false);
    setConfidentialityAgreementAccepted(!!checked);
  };

  const onChangeSearchBoxQuery = (newSearchQuery: string) => {
    setSearchQuery(newSearchQuery);
  };

  const onChangeSearchBoxClear = () => {
    setSearchQuery('');
  };

  const onChangePrivacyTeam = (event: React.FormEvent<HTMLDivElement>,option?: IDropdownOption): void => {
    const value=option?.text as string;
    setPrivacyTeam(value);
    setIsPrivacyTeamError(false);
    if(value==="Other"){
      setCustomPrivacyTeam("");
      setIsCustomPrivacyTeamError(false);
    }
    else{
      setCustomPrivacyTeam("");
      setIsCustomPrivacyTeamError(false);
    }
  };

  const onChangeCustomPrivacyTeam = (event: React.FormEvent<HTMLDivElement | HTMLTextAreaElement>,newValue?: string): void => {
    setCustomPrivacyTeam(newValue || "");
    setIsCustomPrivacyTeamError(false);
  };

  const getEnvironmentErrorMessage = (newValue: any) => {
    if (isRenewalRequest) {
      setEnvRenewalError(false);
      let hasMSIs = false;
      props.subscriberRecord.userAccessTypes?.map((accessType) => {if (accessType.environment == newValue && accessType.servicePrincipals?.length > 0) hasMSIs = true; });
      setEnvClientError(!hasMSIs);
      return !hasMSIs;
    }

    const isInRenewalPeriod = props.subscriberRecord.renewalEnabled && (newValue === 0
      ? (props.subscriberRecord.nonProdSubscriptionExpirationStatus === 1 || props.subscriberRecord.nonProdSubscriptionExpirationStatus === 2)
      : (props.subscriberRecord.prodSubscriptionExpirationStatus === 1 || props.subscriberRecord.prodSubscriptionExpirationStatus === 2));

    if (isInRenewalPeriod) {
      setEnvClientError(true);
      setEnvRenewalError(true);
      return true;
    }
    setEnvRenewalError(false);

    let hasMSIs = false;
    props.subscriberRecord.userAccessTypes?.map((accessType) => {if (accessType.environment == newValue && accessType.servicePrincipals?.length > 0) hasMSIs = true; });
    setEnvClientError(!hasMSIs);
    return !hasMSIs;
  };

  const getNameErrorMessage = (newValue?: string) => {
    if (!newValue) {
      setNameError(true);
      return 'Please enter a valid name with printable characters only.';
    } else if (/^[\x00-\x7F]*$/.test(newValue)) {
      setNameError(false);
      return '';
    } else {
      setNameError(true);
      return 'Please enter a valid name with printable characters only.';
    }
  };

  const getDescriptionErrorMessage = (newValue?: string) => {
    if (!newValue || newValue?.trim().length === 0) {
      setDescriptionError(false);
      return;
    } else if (/^[\x00-\x7F]*$/.test(newValue)) {
      setDescriptionError(false);
      return '';
    } else {
      setDescriptionError(true);
      return 'Please enter a valid description with printable characters only.';
    }
  };

  const getServiceBusNamespaceErrorMessage = async (newValue?: string) => {
    /*
     * Service Bus Namespace Requirements
     * - The namespace can contain only letters, numbers, and hyphens.
     * - The namespace must start with a letter, and it must end with a letter or number.
     * - The namespace must be between 6 and 50 characters long.
     */
    if (!newValue) {
      setServiceBusNamespaceError(true);
      return 'Please enter a valid service bus namespace.';
    } else if (!/^([a-zA-Z0-9-]){6,50}$/.test(newValue)) {
      setServiceBusNamespaceError(true);
      return 'Please enter a valid service bus namespace.';
    }
    let validServiceBus = true;
    setServiceBusNamespaceError(false);
    if (!serviceBusTopicError && serviceBusTopic.length > 0) {
      setIsServiceBusInfoLoading(true);
      validServiceBus = (await validateServiceBus()) as boolean;
    }
    return !validServiceBus
      ? `Invalid ServiceBus configuration. Please re-enter proper values after adding ${__SERVICEBUS_MSI__} to the Service Bus Data Sender role.`
      : '';
  };

  const handleFocusOutEgressOptions = () => {
    if (egressOptions.length == 0) {
      setEgressOptionsError(true);
    } else {
      setEgressOptionsError(false);
    }
  };

  const handleFocusOutGraphQLEgressOptions = () => {
    if (graphEgressOptions == undefined || graphEgressOptions == 0) {
      setGraphEgressOptionsError(true);
    } else {
      setGraphEgressOptionsError(false);
    }
  };

  const getServiceBusTopicErrorMessage = async (newValue?: string) => {
    /*
     * Service Bus Topic Requirements:
     * - Topic names can contain letters, numbers, periods (.), hyphens (-), underscores (_), and slashes (/), up to 260 characters.
     * - Topic names are also case-insensitive.
     */
    if (!newValue) {
      setServiceBusTopicError(true);
      return 'Please enter a valid service bus topic.';
    } else if (!/^([a-zA-Z0-9_/\.-]){1,260}$/.test(newValue)) {
      setServiceBusTopicError(true);
      return 'Please enter a valid service bus topic.';
    }

    setServiceBusTopicError(false);
    let validServiceBus = true;
    if (!serviceBusNamespaceError && serviceBusNamespace.length > 0) {
      setIsServiceBusInfoLoading(true);
      validServiceBus = (await validateServiceBus()) as boolean;
    }
    return !validServiceBus
      ? `Invalid ServiceBus configuration. Please re-enter proper values after adding ${__SERVICEBUS_MSI__} to the Service Bus Data Sender role.`
      : '';
  };

  const handleFocusOutEffectiveDatedCDM = () => {
    if (effectiveDatedCDM.length === 0) {
      setEffectiveDatedCDMError(true);
    } else {
      setEffectiveDatedCDMError(false);
    }
  };

  const getAdditionalNotesErrorMessage = (newValue?: string) => {
    if (/^[\x00-\x7F]*$/.test(newValue) || !newValue) {
      setAdditionalNotesError(false);
      return '';
    } else {
      setAdditionalNotesError(true);
      return 'Please enter valid notes/comments with printable characters only.';
    }
  };

  const onDropdownBlur = (): void => {
    if(!privacyTeam) {
      setIsPrivacyTeamError(true);
    }
    else {
      setIsPrivacyTeamError(false);
    }
  };

  const onCustomPrivacyTeamBlur = (): void => {
   if(privacyTeam === "Other" && !customPrivacyTeam.trim()) {
      setIsCustomPrivacyTeamError(true);
    } else {
       setIsCustomPrivacyTeamError(false);
    }
  };
  
  const deleteAllAttachments = async () => {
    return new Promise<void>((resolve) => {
      httpClient
        .post(`${__API_BASE_URL__}/common/deleteAttachments`, {
          resource: __CLIENT_ID__,
          headers: {
            'Content-Type': 'application/json',
            'X-Content-Type-Options': 'nosniff',
          },
          data: deleteAttachmentRequestList,
        })
        .then(() => {
          resolve();
        })
        .catch((err) => {
          resolve();
        });
    });
  };

  const validateServiceBus = async () => {
    return new Promise<boolean>((resolve) => {
      httpClient
        .get(
          `${__API_BASE_URL__}/common/ValidateServiceBusConfig?serviceBusNameSpace=${serviceBusNamespace}&serviceBusTopic=${serviceBusTopic}`,
          {
            resource: __CLIENT_ID__,
            headers: {
              'Content-Type': 'application/json',
              'X-Content-Type-Options': 'nosniff',
            },
          }
        )
        .then((response: any) => {
          setIsServiceBusInfoLoading(false);
          resolve(response.data);
        })
        .catch((err) => {
          setIsServiceBusInfoLoading(false);
          resolve(false);
        });
    });
  };

  const uploadAllAttachments = async (storeAttachmentRequestList: StoreAttachmentRequest[]) => {
    return new Promise((resolve) => {
      httpClient
        .post(`${__API_BASE_URL__}/common/storeAttachments`, {
          resource: __CLIENT_ID__,
          headers: {
            'Content-Type': 'application/json',
            'X-Content-Type-Options': 'nosniff',
          },
          data: storeAttachmentRequestList,
        })
        .then(({ data }) => {
          resolve(data as StoreAttachmentsResponse[]);
        })
        .catch(() => {
          resolve(null);
        });
    });
  };

  const formatPrivacyApprovalArtifacts = async () => {
    if (deleteAttachmentRequestList.length > 0) {
      await deleteAllAttachments();
    }

    const privacyApprovalArtifactList: PrivacyApprovalArtifact[] = [];

    const storeAttachmentRequestList: StoreAttachmentRequest[] = [];
    attachmentList.forEach((attachment) => {
      // Get all new attachments from the current request
      if (attachment.fileDatabase64) {
        const newStoreAttachmentRequest: StoreAttachmentRequest = {
          folderName: attachment.folderName,
          fileName: attachment.fileName,
          fileDatabase64: attachment.fileDatabase64,
        };
        storeAttachmentRequestList.push(newStoreAttachmentRequest);
        // Get all previous attachments from the draft request
      } else if (attachment.fileUri) {
        const newPrivacyApprovalArtifact: PrivacyApprovalArtifact = {
          folderName: attachment.folderName,
          fileUri: attachment.fileUri,
          fileName: attachment.fileName,
        };
        privacyApprovalArtifactList.push(newPrivacyApprovalArtifact);
      }
    });

    // Upload all new attachments from the current request if they exist
    if (storeAttachmentRequestList.length > 0) {
      const storeAttachmentsResponseList: StoreAttachmentsResponse[] = (await uploadAllAttachments(
        storeAttachmentRequestList
      )) as StoreAttachmentsResponse[];
      if (
        !storeAttachmentsResponseList ||
        storeAttachmentsResponseList.some((storeAttachmentResponse) => storeAttachmentResponse.isUploaded === false)
      ) {
        throw new Error('One or more privacy approval attachments failed to upload.');
      }
      storeAttachmentsResponseList.forEach((storeAttachmentResponse: StoreAttachmentsResponse) => {
        const privacyApprovalArtifact: PrivacyApprovalArtifact = {
          folderName: id,
          fileUri: storeAttachmentResponse.fileUri,
          fileName: storeAttachmentResponse.fileName,
        };
        privacyApprovalArtifactList.push(privacyApprovalArtifact);
      });
    }
    return privacyApprovalArtifactList;
  };

  const onSubmit = async (status: string) => {

    setShowConfirmationPopup(true);

    let privacyApprovalArtifacts: PrivacyApprovalArtifact[];
    try {
      privacyApprovalArtifacts = (await formatPrivacyApprovalArtifacts()) as PrivacyApprovalArtifact[];
    } catch {
      setShowErrorMessage(true);
      return;
    }

    const nodesWithoutApproved = removeApprovedAttributes(formattedSubscriptionNodes);
    const subscriptionNodesWithRequired = addRequiredAttributes(nodesWithoutApproved);
    const subscriptionNodesWithKeys = addKeyAttributes(subscriptionNodesWithRequired);
    const supbscriptionNodesForSubmit = removeSubscriptionNodeFields(subscriptionNodesWithKeys);

    const upsertRequestData = {
      id: id,
      name: name,
      description: description,
      subscriberId: subscriberId,
      requestorUpn: user.email,
      environment: environment,
      status: status,
      submittedDate: new Date().toISOString(), // Modified Date instead
      egressOptions: egressOptions.map(key => (key === 5 || key === 6) && graphEgressOptions ? graphEgressOptions : key),
      serviceBusNamespace: serviceBusNamespace,
      serviceBusTopic: serviceBusTopic,
      effectiveDatedCDM: effectiveDatedCDM,
      PrivacyTeamName: environment === 1 ? (privacyTeam === "Other" ? customPrivacyTeam : privacyTeam) : null,
      privacyApprovalArtifacts: privacyApprovalArtifacts,
      privacyApprovalDate: privacyApprovalDate.toISOString(),
      additionalNotes: additionalNotes,
      subscribedAttributes: supbscriptionNodesForSubmit,
      graphqlAccessType: graphqlAccessType
    };

    httpClient
      .post(`${__API_BASE_URL__}/subscriber/upsertRequest`, {
        resource: __CLIENT_ID__,
        headers: {
          'Content-Type': 'application/json',
          'X-Content-Type-Options': 'nosniff',
        },
        data: upsertRequestData,
      })
      .then(() => {
        setShowSuccessMessage(true);
      })
      .catch(() => {
        setShowErrorMessage(true);
      });
  };

  const submitDismissPanel = () => {
    onSubmit('Submitted');
    dismissPanel();
  };

  const saveAsDraftDismissPanel = () => {
    onSubmit('Draft');
    dismissPanel();
  };

  const onRenderHeader = () => {
    return (
      <PanelHeaderContainer>

        <PanelHeaderTitleText>Add Consumer Request</PanelHeaderTitleText>

        <PivotStyled
          aria-label="Add Consumer Request Panel Pivot"
          selectedKey={selectedPivotKey}
          onLinkClick={handlePivotChange}
          defaultSelectedKey="BasicDetails"
        >
          <PivotItem headerText="Basic Details" itemKey="BasicDetails" />
          <PivotItem headerText="Attribute Subscriptions" itemKey="AttributeSubscriptions" />
        </PivotStyled>

        <HeaderDivider />

        {selectedPivotKey === 'AttributeSubscriptions' && (
          <SearchBoxContainer>
            <SearchBox
              placeholder="Search and Press Enter"
              aria-label="Search for Attribute Subscriptions"
              defaultValue={searchQuery}
              onSearch={(newSearchQuery) => onChangeSearchBoxQuery(newSearchQuery)}
              onClear={onChangeSearchBoxClear}
            />
          </SearchBoxContainer>
        )}
        
      </PanelHeaderContainer>
    );
  };

  const onRenderFooterContent = () => {
    return (
      <AddSubscriberRequestFooterContainer>
        <PrimaryButton
          text="Submit"
          disabled={isSubmitButtonDisabled}
          allowDisabledFocus={true}
          onClick={submitDismissPanel}
        />
        {status != 'Rejected' && (
          <DefaultButton
            text="Save as draft"
            disabled={isSaveAsDraftButtonDisabled}
            allowDisabledFocus={true}
            onClick={saveAsDraftDismissPanel}
          />
        )}
        <DefaultButton text="Cancel" allowDisabledFocus={true} onClick={dismissPanel} />
      </AddSubscriberRequestFooterContainer>
    );
  };

  /*
   * Renders an empty div for now to remove the default navigation section
   */
  const onRenderNavigationEmptyContent = () => {
    return <div></div>;
  };

  return (
    <Panel
      isOpen={isOpen}
      type={PanelType.medium}
      onDismiss={dismissPanel}
      onRenderHeader={onRenderHeader}
      onRenderFooterContent={onRenderFooterContent}
      onRenderNavigation={onRenderNavigationEmptyContent}
      hasCloseButton={false}
      isFooterAtBottom={true}
      isLightDismiss={true}
      styles={stickyFooterPanelStyles}
      data-testid="add-subscriber-request-panel"
      layerProps={{ eventBubblingEnabled: true }}
    >
      {selectedPivotKey == 'BasicDetails' && (
        <AddSubscriberRequestContainer>
          {pivotChangeError && (
            <MessageBar data-testid="message-bar-pivot-change-error" messageBarType={MessageBarType.blocked}>
              Please select an Egress Option before selecting Attributes
            </MessageBar>
          )}
          {envRenewalError && (
            <MessageBar messageBarType={MessageBarType.blocked}>
              This environment is in renewal period. Please use "Renew Subscription" instead.
            </MessageBar>
          )}
          {envClientError && !envRenewalError && (
            <MessageBar data-testid="message-bar-pivot-change-error" messageBarType={MessageBarType.blocked}>
              There is no MSI/SPN configured for the selected environment. Please update your consumer configuration.
            </MessageBar>
          )}
          <ChoiceGroup
            data-testid="add-request-environment"
            label="Environment"
            id="environment"
            placeholder="Select Environment"
            selectedKey={environment === 0 ? 'NonProd' : 'Prod'}
            onChange={onChangeEnvironment}
            options={environmentOptions}
            required={true}
            styles={{ flexContainer: { display: 'flex' } }}
          />
          <TextField
            data-testid="add-request-name"
            label="Request Name"
            id="requestName"
            placeholder="Enter Custom Request Name"
            value={name}
            onChange={onChangeName}
            onGetErrorMessage={getNameErrorMessage}
            required={true}
            validateOnLoad={false}
            validateOnFocusOut={true}
          />
          <TextField
            data-testid="add-request-description"
            label="Description"
            id="description"
            placeholder="Enter Custom Description"
            value={description}
            onChange={onChangeDescription}
            onGetErrorMessage={getDescriptionErrorMessage}
            required={false}
            validateOnLoad={false}
            validateOnFocusOut={true}
            multiline={true}
            autoAdjustHeight={true}
          />
          <ChoiceGroup
            data-testid="add-request-egress-type"
            label="Egress Options"
            id="egressOptionsType"
            selectedKey={egressType}
            onChange={onChangeEgressType}
            options={egressTypeOptions}
            required={true}
            styles={{ flexContainer: { display: 'flex' } }}
          />
          <Dropdown
            data-testid="add-request-egress-options"
            onRenderLabel={onRenderLabelEgressOptions}
            id="egressOptions"
            placeholder="Select Egress Options"
            ariaLabel="Egress Options"
            selectedKeys={egressOptions.map((key) => (key === 6 ? 5 : key))}
            onChange={onChangeEgressOptions}
            onBlur={handleFocusOutEgressOptions}
            errorMessage={egressOptionsError ? 'Please select an Egress Option' : ''}
            options={egressionOptionsDropdown}
            multiSelect={true}
            disabled={egressType === EgressOptionType.Kusto}
          />
          {isServiceBusEgressOptionSelected && (
            <ServiceBusFieldsContainer>
              <TextField
                data-testid="add-request-service-bus-namespace"
                onRenderLabel={onRenderLabelServiceBusNamespace}
                id="serviceBusNameSpace"
                placeholder={'Enter Service Bus Namespace'}
                value={serviceBusNamespace}
                onChange={onChangeServiceBusNamespace}
                onGetErrorMessage={getServiceBusNamespaceErrorMessage}
                validateOnLoad={false}
                validateOnFocusOut={true}
              />
              <TextField
                data-testid="add-request-service-bus-topic"
                onRenderLabel={onRenderLabelServiceBusTopic}
                id="serviceBusTopic"
                placeholder={'Enter Service Bus Topic'}
                value={serviceBusTopic}
                onChange={onChangeServiceBusTopic}
                onGetErrorMessage={getServiceBusTopicErrorMessage}
                validateOnLoad={false}
                validateOnFocusOut={true}
              />
              {isServiceBusInfoLoading && (
                <LoadingContainer>
                  <Spinner size={SpinnerSize.small} />
                </LoadingContainer>
              )}
            </ServiceBusFieldsContainer>
          )}

          {isGraphQLEgressOptionSelected && (
            <Dropdown
              data-testid="add-request-egress-options"
              onRenderLabel={onRenderLabelGraphEgressOptions}
              id="graphEgressOptions"
              placeholder="Select GraphQL Egress Options"
              ariaLabel="GraphQL Egress Options"
              selectedKey={graphEgressOptions}
              onChange={onChangeGraphQLEgressOptions}
              onBlur={handleFocusOutGraphQLEgressOptions}
              errorMessage={graphEgressOptionsError ? 'Please select an GraphQL Egress Option' : ''}
              options={graphEgressionOptionsDropdown}
              multiSelect={false}
          />
          )}
          
          {graphQLInfoMessage && (
            <MessageBar
              messageBarType={MessageBarType.info}
              isMultiline={false}
              truncated={true}
              styles={{ root: { marginTop: 4 } }}
            >
              {graphQLInfoMessage}
            </MessageBar>
          )}

          <Dropdown
            data-testid="add-request-effective-dated-cdm"
            onRenderLabel={onRenderLabelEffectiveDatedCdm}
            id="effectiveDatedCDM"
            placeholder="Select Effective-Dated CDM"
            ariaLabel="Effective-Dated CDM"
            selectedKey={effectiveDatedCDM}
            options={effectiveDatedCDMOptions}
            onChange={onChangeEffectiveDatedCDM}
            onBlur={handleFocusOutEffectiveDatedCDM}
            errorMessage={effectiveDatedCDMError ? 'Please select an Effective-Dated CDM' : ''}
            multiSelect={false}
          />

          {/* Hide privacy/approval fields if skipPrivacyRequirements is true */}
          {environment == 1 && !(
            isOnlyEO14117Selected(formattedSubscriptionNodes)
          ) && <>
            <Checkbox
              ariaLabel="HR-Owned Data Access Agreement"
              data-testid="add-request-privacy-confidentiality-agreement"
              onChange={handleConfidentialityCheckbox}
              checked={confidentialityAgreementAccepted}
              onRenderLabel={() => (
                <>
                  By proceeding, you confirm that you have read and agree to comply with the<br/>
                  <a
                    href={__HRDP_CONFIDENTIALITY_URL__}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      setHasClickedAgreementLink(true);
                      setShowAgreementWarning(false);
                    }}
                  >
                    HR-Owned Data Access Agreement
                  </a>
                  . Non-compliance may result in restricted access, policy violations, or employment-related consequences. This agreement must be signed for each individual request.
                </>
              )}
            />
            {showAgreementWarning && (
              <MessageBar
                messageBarType={MessageBarType.warning}
                isMultiline={false}
                data-testid="message-bar-agreement-warning"
                role="alert"
                styles={{ root: { marginTop: 8, marginBottom: 8 } }}
              >
                Please click the agreement link above before checking this box.
              </MessageBar>
            )}

            <Dropdown
              data-testid="add-request-privacy-team-name"
              onRenderLabel={onRenderLabelPrivacyTeam}
              id={privacyTeam}
              placeholder='Select Privacy Team'
              label="Authorized Privacy Team"
              selectedKey={privacyTeamOptions.find(o => o.text === privacyTeam)?.key}
              options={privacyTeamOptions}
              onChange={onChangePrivacyTeam}
              onBlur={onDropdownBlur}
              errorMessage={isPrivacyTeamError ? 'Please select a Privacy Team': undefined}
            />

            {privacyTeam === "Other" && (
             <TextField
              onRenderLabel={onRenderLabelOtherPrivacyTeam}
              placeholder='Enter Privacy Team Name'
              value={customPrivacyTeam}
              label='Other Privacy Team'
              onChange={onChangeCustomPrivacyTeam}
              onBlur={onCustomPrivacyTeamBlur}
              errorMessage={isCustomPrivacyTeamError? 'Please enter a Privacy Team': undefined}
             />
            )} 

            {onRenderLabelPrivacyApprovalArtifacts()}
            <AttachmentsForm
              requestId={id}
              attachmentList={attachmentList}
              setAttachmentList={setAttachmentList}
              deleteAttachmentRequestList={deleteAttachmentRequestList}
              setDeleteAttachmentRequestList={setDeleteAttachmentRequestList}
            />

            {onRenderLabelPrivacyApprovalDate()}
            <DatePicker
              data-testid="add-request-privacy-approval-date"
              id="privacyApprovalDate"
              placeholder="Select a date..."
              ariaLabel="Privacy Approval Date"
              value={privacyApprovalDate}
              onSelectDate={onChangePrivacyApprovalDate}
              maxDate={new Date()}
              firstDayOfWeek={DayOfWeek.Sunday}
              firstWeekOfYear={1}
              showMonthPickerAsOverlay={true}
              showGoToToday={false}
              strings={defaultDatePickerStrings}
            />
          </>}
          <TextField
            data-testid="add-request-privacy-additional-notes"
            label="Additional Notes"
            id="additionalNotes"
            placeholder="Enter Additional Notes"
            value={additionalNotes}
            onChange={onChangeAdditionalNotes}
            onGetErrorMessage={getAdditionalNotesErrorMessage}
            required={false}
            validateOnLoad={false}
            validateOnFocusOut={true}
            multiline={true}
            autoAdjustHeight={true}
            rows={3}
          />
        </AddSubscriberRequestContainer>
      )}
      {selectedPivotKey == 'AttributeSubscriptions' && (
        <TenantSubscriptions
          formattedSubscriptionNodes={displayFormattedNodes}
          setFormattedSubscriptionNodes={setFormattedSubscriptionNodes}
          filteredSubscriptionNodes={displayFilteredNodes}
          setFilteredSubscriptionNodes={setFilteredSubscriptionNodes}
          isAttributeSubscriptionLoading={isAttributeSubscriptionLoading || isApprovedAttributeSubscriptionLoading}
          searchQuery={searchQuery}
          checkedAttributeCount={displayCheckedCount}
          setCheckedAttributeCount={setCheckedAttributeCount}
          egressType={egressType}
        />
      )}
    </Panel>
  );
}


function isOnlyEO14117Selected(formattedSubscriptionNodes: any[]): boolean {
  let eoChecked = false;
  let onlyEO14117Checked = true;

  function traverse(node: any, underEO14117 = false) {
    if (node.name === 'EO14117' && node.children) {
      node.children.forEach((child: any) => traverse(child, true));
    } else if (node.children) {
      node.children.forEach((child: any) => traverse(child, false));
    } else if (node.isCheckedAttribute) {
      if (underEO14117) {
        eoChecked = true;
      } else {
        onlyEO14117Checked = false;
      }
    }
  }

  formattedSubscriptionNodes.forEach(node => traverse(node, false));
  return eoChecked && onlyEO14117Checked;
}

const PanelHeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const PanelHeaderTitleText = styled(Text)`
  font-weight: 700;
  font-size: 26px;
  line-height: 32px;
  color: #171717;
  padding-top: 32px;
  padding-bottom: 16px;
  padding-left: 48px;
  padding-right: 48px;
`;

const PivotStyled = styled(Pivot)`
  display: flex;
  margin-left: 40px;
`;

const HeaderDivider = styled.div`
  display: flex;
  border-bottom: 1px solid #e5e5e5;
  margin-top: -6px;
`;

const SearchBoxContainer = styled.div`
  padding-top: 32px;
  padding-bottom: 32px;
  padding-left: 48px;
  padding-right: 48px;
  border-bottom: 1px solid #e5e5e5;
`;

const AddSubscriberRequestContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 20px;
  padding-top: 32px;
  padding-bottom: 16px;
  padding-left: 24px;
  padding-right: 24px;
`;

const AddSubscriberRequestFooterContainer = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: 16px;
`;

const ServiceBusFieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 16px;
`;

const LoadingContainer = styled.div`
  display: flex;
  height: 24px;
  align-content: center;
  justify-content: center;
  padding-left: 4px;
`;
